import { useState, useEffect } from 'react';
import { auth, db, storage } from '../firebase';

export default function FirebaseConnectionTest() {
  const [connectionStatus, setConnectionStatus] = useState({
    firebase: 'checking',
    auth: 'checking',
    firestore: 'checking',
    storage: 'checking'
  });
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    testFirebaseConnection();
  }, []);

  const testFirebaseConnection = async () => {
    try {
      setConnectionStatus(prev => ({ ...prev, firebase: 'testing' }));
      
      const firebaseConfig = {
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.REACT_APP_FIREBASE_APP_ID
      };
      
      setConfig(firebaseConfig);
      
      const hasValidConfig = Object.values(firebaseConfig).every(value => 
        value && value !== 'your-api-key-here' && value !== 'your-sender-id' && value !== 'your-app-id'
      );
      
      if (!hasValidConfig) {
        throw new Error('Firebase configuration not properly set. Please update your .env file.');
      }
      
      setConnectionStatus(prev => ({ ...prev, firebase: 'connected' }));
      
      setConnectionStatus(prev => ({ ...prev, auth: 'testing' }));
      try {
        if (auth) {
          setConnectionStatus(prev => ({ ...prev, auth: 'connected' }));
        } else {
          throw new Error('Auth not initialized');
        }
      } catch (authError) {
        console.error('Auth test failed:', authError);
        setConnectionStatus(prev => ({ ...prev, auth: 'error' }));
      }
      
      setConnectionStatus(prev => ({ ...prev, firestore: 'testing' }));
      try {
        if (db) {
          setConnectionStatus(prev => ({ ...prev, firestore: 'connected' }));
        } else {
          throw new Error('Firestore not initialized');
        }
      } catch (firestoreError) {
        console.error('Firestore test failed:', firestoreError);
        setConnectionStatus(prev => ({ ...prev, firestore: 'error' }));
      }
      
      setConnectionStatus(prev => ({ ...prev, storage: 'testing' }));
      try {
        if (storage) {
          setConnectionStatus(prev => ({ ...prev, storage: 'connected' }));
        } else {
          throw new Error('Storage not initialized');
        }
      } catch (storageError) {
        console.error('Storage test failed:', storageError);
        setConnectionStatus(prev => ({ ...prev, storage: 'error' }));
      }
      
    } catch (error) {
      console.error('Firebase connection test failed:', error);
      setError(error.message);
      setConnectionStatus({
        firebase: 'error',
        auth: 'error',
        firestore: 'error',
        storage: 'error'
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return 'Connected';
      case 'testing': return 'Testing...';
      case 'checking': return 'Checking...';
      case 'error': return 'Error';
      default: return 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'text-green-600';
      case 'testing':
      case 'checking': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const allConnected = Object.values(connectionStatus).every(status => status === 'connected');
  const hasErrors = Object.values(connectionStatus).some(status => status === 'error');

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Firebase Connection Test</h2>
          <p className="mt-2 text-gray-600">
            Test your Firebase configuration and connection status.
          </p>
        </div>
        
        <div className="p-6">
          <div className={`p-4 rounded-lg mb-6 ${
            allConnected ? 'bg-green-50 border border-green-200' :
            hasErrors ? 'bg-red-50 border border-red-200' :
            'bg-yellow-50 border border-yellow-200'
          }`}>
            <div className="flex items-center">
              <span className="text-2xl mr-3">
                {allConnected ? '🎉' : hasErrors ? '⚠️' : '🔄'}
              </span>
              <div>
                <h3 className={`text-lg font-semibold ${
                  allConnected ? 'text-green-800' :
                  hasErrors ? 'text-red-800' :
                  'text-yellow-800'
                }`}>
                  {allConnected ? 'All Services Connected!' :
                   hasErrors ? 'Connection Issues Detected' :
                   'Testing Connections...'}
                </h3>
                <p className={`text-sm ${
                  allConnected ? 'text-green-700' :
                  hasErrors ? 'text-red-700' :
                  'text-yellow-700'
                }`}>
                  {allConnected ? 'Your Firebase backend is ready to use.' :
                   hasErrors ? 'Please check your Firebase configuration.' :
                   'Please wait while we test your connections.'}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">Firebase Core</span>
                <span className={`text-sm font-medium ${getStatusColor(connectionStatus.firebase)}`}>
                  {getStatusIcon(connectionStatus.firebase)}
                </span>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">Authentication</span>
                <span className={`text-sm font-medium ${getStatusColor(connectionStatus.auth)}`}>
                  {getStatusIcon(connectionStatus.auth)}
                </span>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">Firestore Database</span>
                <span className={`text-sm font-medium ${getStatusColor(connectionStatus.firestore)}`}>
                  {getStatusIcon(connectionStatus.firestore)}
                </span>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">Cloud Storage</span>
                <span className={`text-sm font-medium ${getStatusColor(connectionStatus.storage)}`}>
                  {getStatusIcon(connectionStatus.storage)}
                </span>
              </div>
            </div>
          </div>

          {config && (
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Configuration Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Project ID:</span>
                  <span className="ml-2 text-gray-600">{config.projectId}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Auth Domain:</span>
                  <span className="ml-2 text-gray-600">{config.authDomain}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Storage Bucket:</span>
                  <span className="ml-2 text-gray-600">{config.storageBucket}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">API Key:</span>
                  <span className="ml-2 text-gray-600">
                    {config.apiKey ? `${config.apiKey.substring(0, 10)}...` : 'Not set'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-medium text-red-800 mb-2">Error Details</h3>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {allConnected && (
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-green-800 mb-3">Next Steps</h3>
              <div className="text-green-700 text-sm space-y-2">
                <p>✅ Your Firebase backend is properly configured!</p>
                <p><strong>What you can do now:</strong></p>
                <p className="ml-4">• Test the full backend: <a href="/firebase-test" className="underline">Run Firebase Tests</a></p>
                <p className="ml-4">• Create an account: <a href="/signup" className="underline">Sign Up</a></p>
                <p className="ml-4">• Deploy security rules: npm run deploy-rules</p>
                <p className="ml-4">• Set up sample data: npm run setup-firebase</p>
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-center">
            <button
              onClick={testFirebaseConnection}
              className="px-6 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Test Connection Again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
